import React, { useRef, useState } from 'react';
import { verificationApi } from '../api/verificationApi';
import { Alert, Badge, Button, Card, cn } from './ui';

const ACCEPT_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf',
];
const ACCEPT = ACCEPT_TYPES.join(',');
const MAX_BYTES = 10 * 1024 * 1024;

const STATUS_META = {
  not_submitted: { label: 'Not submitted', variant: 'neutral', border: 'border-slate-200' },
  pending: { label: 'Under review', variant: 'warning', border: 'border-amber-200' },
  approved: { label: 'Verified', variant: 'success', border: 'border-emerald-200' },
  rejected: { label: 'Rejected', variant: 'danger', border: 'border-red-200' },
};

const fmtDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const prettyMime = (mime = '') => mime.toUpperCase().replace('APPLICATION/', '').replace('IMAGE/', '');

const VerificationDocumentUpload = ({ type, title, description, doc, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const status = doc?.status || 'not_submitted';
  const meta = STATUS_META[status] || STATUS_META.not_submitted;
  const canReplace = status !== 'approved';
  const hasFile = Boolean(doc?.url);

  const handlePickClick = () => {
    if (!uploading && canReplace) inputRef.current?.click();
  };

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    if (!ACCEPT_TYPES.includes(file.type)) return setError('Unsupported file type. Use JPG, PNG, WEBP, HEIC or PDF.');
    if (file.size > MAX_BYTES) return setError(`File too large. Max ${MAX_BYTES / 1024 / 1024} MB.`);

    setUploading(true);
    setProgress(0);
    try {
      const uploadRes = await verificationApi.uploadFile(file, {
        kind: type,
        onProgress: (value) => setProgress(value),
      });
      if (!uploadRes?.url) throw new Error('Upload failed: no URL returned.');
      const saveRes = await verificationApi.submitDocument({
        type,
        url: uploadRes.url,
        originalName: file.name,
        mimeType: file.type,
      });
      onChange?.(saveRes.data);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setError('');
    setUploading(true);
    try {
      const res = await verificationApi.removeDocument(type);
      onChange?.(res.data);
      setConfirmingRemove(false);
    } catch (err) {
      setError(err.message || 'Failed to remove document.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className={cn('border shadow-none', meta.border)}>
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="m-0 font-nunito text-sm font-extrabold text-giggs-ink">{title}</h3>
          <p className="mb-0 mt-1 font-dm text-xs leading-5 text-giggs-muted">{description}</p>
        </div>
        <Badge variant={meta.variant}>{meta.label}</Badge>
      </div>

      {status === 'rejected' && doc?.rejectionReason && (
        <div className="px-4 pb-3"><Alert variant="danger" title="Reason:">{doc.rejectionReason}</Alert></div>
      )}

      <div className="px-4 pb-4">
        {hasFile ? (
          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-center">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-white text-giggs-green shadow-sm">
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>
            </div>
            <div className="min-w-0 flex-1">
              <span className="block truncate font-dm text-[13px] font-semibold text-giggs-ink">{doc.originalName || 'Document'}</span>
              <span className="mt-0.5 block font-dm text-[11px] text-giggs-muted">
                {prettyMime(doc.mimeType) || 'FILE'}{doc.submittedAt && ` · Uploaded ${fmtDate(doc.submittedAt)}`}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {doc.url && <a href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[34px] items-center rounded-lg border border-slate-200 bg-white px-3 font-dm text-xs font-semibold text-giggs-green no-underline hover:border-giggs-green">View</a>}
              {canReplace && <Button size="sm" variant="outline" onClick={handlePickClick} disabled={uploading}>Replace</Button>}
              {canReplace && <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setConfirmingRemove(true)} disabled={uploading}>Remove</Button>}
            </div>
          </div>
        ) : (
          <button type="button" onClick={handlePickClick} disabled={uploading} className="flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-7 text-giggs-muted transition hover:border-giggs-green hover:bg-giggs-green/[0.035] hover:text-giggs-green disabled:cursor-not-allowed disabled:opacity-50">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>
            <span className="mt-2 font-dm text-sm font-semibold text-giggs-text">Click to upload</span>
            <span className="mt-1 font-dm text-[11px]">JPG, PNG, WEBP, HEIC or PDF · Max 10 MB</span>
          </button>
        )}

        {uploading && (
          <div className="mt-3 flex items-center gap-2">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-giggs-green transition-[width] duration-200" style={{ width: `${progress}%` }} />
            </div>
            <span className="w-9 text-right font-dm text-[11px] font-semibold text-giggs-muted">{progress}%</span>
          </div>
        )}

        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

        <input ref={inputRef} type="file" accept={ACCEPT} onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} className="hidden" />

        {confirmingRemove && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3">
            <p className="m-0 font-dm text-xs leading-5 text-red-800">Remove this document? You'll need to upload a new one to keep your verification active.</p>
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirmingRemove(false)} disabled={uploading}>Cancel</Button>
              <Button size="sm" variant="danger" onClick={handleRemove} disabled={uploading}>{uploading ? 'Removing…' : 'Yes, remove'}</Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default VerificationDocumentUpload;
